import React from 'react';
import { useTranslations } from '../../locales/useTranslations';
import { BiCheckCircle } from 'react-icons/bi';
import Image from 'next/image';
import dynamic from 'next/dynamic';
import 'leaflet/dist/leaflet.css';

// Dynamischer Import für Leaflet-Karte (nur Client-Side)
const OfficeMap = dynamic(
    () => import('./OfficeMap'),
    { ssr: false }
);

const KontaktExtendedInfo = () => {
    const { t } = useTranslations();

    const serviceAreas = [
        {
            image: '/images/karriere/software.webp',
            title: t('contact_services_software'),
            desc: t('contact_services_software_desc')
        },
        {
            image: '/images/karriere/analytics.webp',
            title: t('contact_services_analytics'),
            desc: t('contact_services_analytics_desc')
        },
        {
            image: '/images/karriere/robot.webp',
            title: t('contact_services_automation'),
            desc: t('contact_services_automation_desc')
        },
        {
            image: '/images/karriere/beratung.webp',
            title: t('contact_services_consulting'),
            desc: t('contact_services_consulting_desc')
        },
        {
            image: '/images/karriere/web.webp',
            title: t('contact_services_web'),
            desc: t('contact_services_web_desc')
        },
        {
            image: '/images/karriere/apps.webp',
            title: t('contact_services_app'),
            desc: t('contact_services_app_desc')
        }
    ];

    const faqItems = [
        { question: t('contact_faq_question1'), answer: t('contact_faq_answer1') },
        { question: t('contact_faq_question2'), answer: t('contact_faq_answer2') },
        { question: t('contact_faq_question3'), answer: t('contact_faq_answer3') },
        { question: t('contact_faq_question4'), answer: t('contact_faq_answer4') }
    ];

    return (
        <section className="contact-extended-info pt-100 pb-70 kontakt-extended">
            {/* Dekorative Hintergrund-Elemente */}
            <div className="kontakt-extended__decoration"></div>

            <div className="container kontakt-extended__container">
                {/* Service-Bereiche */}
                <div className="row mb-5">
                    <div className="col-12">
                        <div className="section-title text-center mb-5">
                            <span className="kontakt-extended__badge">
                                {t('contact_services_title')}
                            </span>
                            <h2 className="kontakt-extended__section-title">
                                {t('contact_services_subtitle')}
                            </h2>
                        </div>
                    </div>
                </div>

                <div className="row mb-5">
                    {serviceAreas.map((service, index) => (
                        <div key={index} className="col-lg-4 col-md-6 mb-4">
                            <div className="kontakt-extended__service-card">
                                <div className="kontakt-extended__service-decoration"></div>
                                <div className="kontakt-extended__service-content">
                                    <div className="kontakt-extended__service-image">
                                        <Image
                                            src={service.image}
                                            alt={service.title}
                                            fill
                                            style={{ objectFit: 'cover' }}
                                            sizes="(max-width: 768px) 100vw, (max-width: 1200px) 50vw, 33vw"
                                        />
                                    </div>
                                    <h3 className="kontakt-extended__service-title">
                                        {service.title}
                                    </h3>
                                    <p className="kontakt-extended__service-desc">
                                        {service.desc}
                                    </p>
                                </div>
                            </div>
                        </div>
                    ))}
                </div>

                {/* FAQ - Volle Breite */}
                <div className="row mb-5">
                    <div className="col-12">
                        <div className="kontakt-extended__faq-container">
                            <div className="section-title text-center mb-5">
                                <span className="kontakt-extended__badge">
                                    {t('contact_faq_title')}
                                </span>
                            </div>

                            <div className="row">
                                {faqItems.map((faq, index) => (
                                    <div key={index} className="col-lg-6 mb-4">
                                        <div className="kontakt-extended__faq-item">
                                            <h4 className="kontakt-extended__faq-question">
                                                <BiCheckCircle className="kontakt-extended__faq-icon" />
                                                {faq.question}
                                            </h4>
                                            <p className="kontakt-extended__faq-answer">
                                                {faq.answer}
                                            </p>
                                        </div>
                                    </div>
                                ))}
                            </div>
                        </div>
                    </div>
                </div>

                {/* Karte */}
                <div className="row mb-5">
                    <div className="col-12">
                        <div className="kontakt-extended__map-container">
                            <div className="section-title text-center mb-4">
                                <span className="kontakt-extended__badge">
                                    {t('contact_map_title') || 'Unser Standort'}
                                </span>
                                <h3 className="kontakt-extended__map-title">
                                    {t('contact_map_subtitle') || 'Besuchen Sie uns in Kiel'}
                                </h3>
                                <p className="kontakt-extended__map-address">
                                    Königsweg 48, 24114 Kiel
                                </p>
                            </div>
                            <OfficeMap />
                        </div>
                    </div>
                </div>

                {/* Zusätzliche Inhalte: Standort & Kontaktwege */}
                <div className="row">
                    <div className="col-lg-4 col-md-6 mb-4">
                        <div className="kontakt-extended__info-card">
                            <h4 className="kontakt-extended__info-title">
                                {t('contact_location_title')}
                            </h4>
                            <p className="kontakt-extended__info-text">
                                {t('contact_location_address_line1')}<br />
                                {t('contact_location_address_line2')}<br />
                                {t('contact_location_address_country')}
                            </p>
                            <a
                                href="https://www.openstreetmap.org/?mlat=54.3139654&mlon=10.1260107#map=17/54.3139654/10.1260107"
                                className="kontakt-extended__info-link kontakt-extended__info-link--secondary"
                                target="_blank"
                                rel="noopener noreferrer"
                            >
                                {t('contact_location_directions')}
                            </a>
                        </div>
                    </div>

                    <div className="col-lg-4 col-md-6 mb-4">
                        <div className="kontakt-extended__info-card">
                            <h4 className="kontakt-extended__info-title">
                                {t('contact_email_title')}
                            </h4>
                            <p className="kontakt-extended__info-text">
                                <a href="mailto:info@simplify-data.de" className="kontakt-extended__info-link">
                                    info@simplify-data.de
                                </a>
                            </p>
                        </div>
                    </div>

                    <div className="col-lg-4 col-md-6 mb-4">
                        <div className="kontakt-extended__info-card">
                            <h4 className="kontakt-extended__info-title">
                                {t('contact_telephone_title')}
                            </h4>
                            <p className="kontakt-extended__info-text">
                                <a href="tel:+4943170577024" className="kontakt-extended__info-link">
                                    {t('contact_telephone_number')}
                                </a>
                            </p>
                        </div>
                    </div>
                </div>
            </div>
        </section>
    );
}

export default KontaktExtendedInfo;
